
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../firebase';
import { Product, Ingredient, MenuConfig, CategoryConfig, GlobalSettings, DeliverySettings, MenuGraphicsSettings, LiveOrder, OrderContext, MenuType, CartItem, DeliveryZone, OpeningDay, PrinterSettings, SocialLinks, PublicOrderStat, Language, RiderProfile } from '../types';
import { subscribeToProducts, subscribeToIngredients, subscribeToMenus, subscribeToCategories, subscribeToSettings, subscribeToDeliverySettings, subscribeToGraphicsSettings, subscribeToPendingOrders, subscribeToPublicStats, getAdminTenant, getTenantId, getCustomerByPhone, subscribeToOrderHistory, subscribeToOrdersSince, updateGlobalSettings, subscribeToRiders, getTenantMetadata, logSiteVisit, unlogSiteVisit } from '../services/dbService';
import { DEFAULT_TAKE_AWAY_FEE, DEFAULT_DISPOSABLE_FEE, DEFAULT_LOYALTY_SETTINGS, DEFAULT_SERVING_OPTIONS } from '../constants';
import { Loader2 } from 'lucide-react';
import { useAppStore } from '../src/store/useAppStore';
import { playSynthesizedSound, unlockAudioContext } from '../utils/audioUtils';
import { getWorkingDayBoundaries } from '../utils/shopStatus';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../utils/translations';
import { getStandardTranslation } from '../utils/standardMenuTranslations';

// Define the Context Type
interface AppContextType {
  // Navigation
  view: string;
  setView: (view: 'intro' | 'login' | 'menu-type' | 'base-select' | 'builder' | 'cart' | 'admin' | 'tracking' | 'rider-locked' | 'rider' | 'tv-menu' | 'tv-pairing') => void;
  adminSubView: string;
  setAdminSubView: (view: any) => void;
  trackingOrderId: string | null;
  setTrackingOrderId: (id: string | null) => void;
  
  // UI Flags
  isOrderListOpen: boolean;
  setIsOrderListOpen: (isOpen: boolean) => void;
  isKioskAttractActive: boolean;
  setIsKioskAttractActive: (isActive: boolean) => void;
  
  // Data
  user: User | null;
  currentUserTenantId: string | null;
  currentUserCity: string | null;
  products: Product[];
  ingredients: Ingredient[];
  menus: MenuConfig[];
  categories: CategoryConfig[];
  settings: GlobalSettings;
  deliverySettings: DeliverySettings;
  graphicsSettings: MenuGraphicsSettings;
  liveOrders: LiveOrder[]; 
  publicStats: PublicOrderStat[]; 
  
  // Order History (Global State)
  orderHistory: LiveOrder[];
  historyFilterMode: 'LAST_500' | 'TODAY' | 'WEEK' | 'MONTH' | 'ALL';
  setHistoryFilterMode: (mode: 'LAST_500' | 'TODAY' | 'WEEK' | 'MONTH' | 'ALL') => void;
  historyLimit: number;
  setHistoryLimit: (limit: number | ((prev: number) => number)) => void;
  isHistoryLoading: boolean;
  setHistorySubscriptionActive: (active: boolean) => void;

  // Optimistic UI
  optimisticCompletedIds: string[]; 
  handleOptimisticComplete: (orderId: string) => void;
  revertOptimisticComplete: (orderId: string) => void;
  pendingOrdersCount: number; 

  // Loading & Sync
  initialLoading: boolean;
  menuLoading: boolean;
  isDataReady: boolean; // NEW: Track if products/menus are loaded
  riders: RiderProfile[]; // NEW: Global rider data
  isSuperUser: boolean; // NEW: Global Super Admin flag
  settingsLoaded: boolean;
  graphicsLoaded: boolean;
  
  // Order State
  orderContext: OrderContext;
  setOrderContext: (ctx: OrderContext) => void;
  pendingContext: OrderContext | null;
  setPendingContext: (ctx: OrderContext | null) => void;
  menuType: MenuType;
  setMenuType: (type: MenuType) => void;
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  cartTotal: number;
  promoQuantityDiscount: number; // NEW FOR 3x2, etc.
  smallOrderSurcharge: number; // NEW
  
  // Delivery State
  deliveryAddress: string;
  setDeliveryAddress: (addr: string) => void;
  validatedDelivery: { zone: DeliveryZone; distance: number; address: string } | null;
  setValidatedDelivery: (val: any) => void;
  intercomName: string;
  setIntercomName: (name: string) => void;
  
  // Customer Info
  customerName: string;
  setCustomerName: (name: string) => void;
  customerPhone: string;
  setCustomerPhone: (phone: string) => void;
  customerBirthDate: string;
  setCustomerBirthDate: (date: string) => void;
  paymentMethod: 'CASH' | 'POS' | 'NEXI' | 'SATISPAY' | 'PAYPAL' | null;
  setPaymentMethod: (method: 'CASH' | 'POS' | 'NEXI' | 'SATISPAY' | 'PAYPAL' | null) => void;
  
  // Loyalty State
  loyaltyPoints: number; 
  setLoyaltyPoints: (p: number) => void;
  redeemLoyalty: boolean;
  setRedeemLoyalty: (v: boolean) => void;
  hasActiveSuperReward: boolean; 
  setHasActiveSuperReward: (v: boolean) => void;
  superRewardExpiryDate: number | null; 
  setSuperRewardExpiryDate: (date: number | null) => void;

  // Builder/Selection State
  selectedProduct: Product | null;
  setSelectedProduct: (p: Product | null) => void;
  selectedIngredients: Ingredient[];
  setSelectedIngredients: React.Dispatch<React.SetStateAction<Ingredient[]>>;
  editingCartId: string | null;
  setEditingCartId: (id: string | null) => void;
  editingOrderId: string | null;
  setEditingOrderId: (id: string | null) => void;
  servingStyle: string | null;
  setServingStyle: (style: string | null) => void;
  
  // Helpers
  playNotificationSound: () => void;
  showToast: (msg: string) => void;
  toast: { show: boolean; message: string };
  isKioskMode: boolean;
  setIsKioskMode: (val: boolean) => void;
  isProRetailMode: boolean; 
  shopStatus: { isOpen: boolean; currentSchedule: string };
  isDeliveryAvailable: boolean;
  
  // Device Settings
  isDeviceMuted: boolean;
  setIsDeviceMuted: (val: boolean) => void;

  // Multi-language
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  resolveLang: (obj: any, field: string) => string;

  // NEW: Session Cleanup
  clearSession: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const getStorageKey = (baseKey: string) => {
    const tenantId = getTenantId() || 'root'; 
    return `${tenantId}_${baseKey}`;
};

const loadState = <T,>(baseKey: string, fallback: T): T => {
    try {
        const item = localStorage.getItem(getStorageKey(baseKey));
        return item ? JSON.parse(item) : fallback;
    } catch {
        return fallback;
    }
};

const saveState = (baseKey: string, value: any) => {
    try {
        localStorage.setItem(getStorageKey(baseKey), JSON.stringify(value));
    } catch (e) {
        console.error("Storage Error", e);
    }
};

const DEFAULT_SETTINGS_OBJ: GlobalSettings = {
    appName: "Ordina Ora", 
    whatsappNumber: "",
    takeAwayFee: DEFAULT_TAKE_AWAY_FEE,
    disposableFee: DEFAULT_DISPOSABLE_FEE,
    deliveryDiscount: 0,
    categoryOrder: [],
    socialLinks: {},
    openingHours: [],
    specialClosures: [],
    isDeliveryEnabled: true,
    printerSettings: { ipAddress: '', port: '9100', paperWidth: '80mm' },
    soundSettings: { enabled: true, volume: 0.5, soundType: 'doorbell' },
    nexiSettings: { isEnabled: false, alias: '', macKey: '', mode: 'TEST' },
    satispaySettings: { 
        isEnabled: false, 
        mode: 'LINK', 
        link: '', 
        apiEnv: 'SANDBOX', 
        activationCode: '', 
        rsaKey: '', 
        keyId: '' 
    },
    paypalSettings: { 
        isEnabled: false, 
        mode: 'LINK', 
        link: '', 
        apiEnv: 'SANDBOX', 
        clientId: '', 
        clientSecret: '' 
    },
    loyaltyProgram: DEFAULT_LOYALTY_SETTINGS,
    coupons: [],
    servingOptions: DEFAULT_SERVING_OPTIONS
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // VERSION KEY TO FORCE RE-SUBSCRIBE
  const [tenantVersion, setTenantVersion] = useState(0);

  // Time Tick state to trigger automatic open/close state evaluation
  const [timeTick, setTimeTick] = useState<number>(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeTick(Date.now());
    }, 15000); // 15 seconds
    return () => clearInterval(intervalId);
  }, []);

  const [view, _setView] = useState<'intro' | 'login' | 'menu-type' | 'base-select' | 'builder' | 'cart' | 'admin' | 'tracking' | 'rider-locked' | 'rider' | 'tv-menu' | 'tv-pairing'>(() => {
      if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('view') === 'rider-locked') return 'rider-locked';
          if (urlParams.get('view') === 'admin') return 'admin';
          if (urlParams.get('view') === 'tv-menu') return 'tv-menu';
          if (urlParams.get('trackOrder')) return 'tracking';
          
          if (window.location.pathname === '/tv' || window.location.pathname === '/tv/') {
              return 'tv-pairing';
          }
          if (window.location.pathname.startsWith('/track/')) {
              return 'tracking';
          }
      }
      const saved = loadState<string>('app_view', 'intro');
      // Only reset 'login' and 'tracking' to 'intro'. 
      // Keep 'admin' if it was saved, so we don't flash the client app on refresh.
      if (saved === 'login' || saved === 'tracking') return 'intro';
      return saved as any;
  });

  const [adminSubView, _setAdminSubView] = useState('dashboard');
  const setAdminSubView = (view: any) => {
      _setAdminSubView(view);
      window.scrollTo(0, 0);
  };
  const [isOrderListOpen, setIsOrderListOpen] = useState(false); 
  const [isKioskAttractActive, setIsKioskAttractActive] = useState(false);
  
  const setView = (newView: any) => {
      if (newView === view) return;
      try { window.history.pushState({ view: newView }, ''); } catch (e) { console.debug(e); }
      _setView(newView);
      window.scrollTo(0, 0);
  };

  const [settings, setSettings] = useState<GlobalSettings>(() => loadState('app_settings', DEFAULT_SETTINGS_OBJ));
  
  useEffect(() => {
      if (settings.isKioskMode) {
          setIsKioskAttractActive(true);
      }
  }, [settings.isKioskMode]);
  
  const clearSession = () => {
      setCustomerName('');
      setCustomerPhone('');
      setCustomerBirthDate('');
      setIntercomName('');
      setDeliveryAddress('');
      setPaymentMethod(null);
      setCart([]);
      setRedeemLoyalty(false);
      setValidatedDelivery(null);
      setLoyaltyPoints(0);
      setHasActiveSuperReward(false);
      
      // Force clear specific local storage keys
      localStorage.removeItem(getStorageKey('app_customer_name'));
      localStorage.removeItem(getStorageKey('app_customer_phone'));
      localStorage.removeItem(getStorageKey('app_delivery_address'));
      localStorage.removeItem(getStorageKey('app_intercom'));
      localStorage.removeItem(getStorageKey('app_cart'));
  };

  const clearSessionRef = useRef(clearSession);
  const setViewRef = useRef(setView);
  const setIsKioskAttractActiveRef = useRef(setIsKioskAttractActive);

  useEffect(() => {
      clearSessionRef.current = clearSession;
      setViewRef.current = setView;
      setIsKioskAttractActiveRef.current = setIsKioskAttractActive;
  });

  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
      const isKiosk = settings.isKioskMode || localStorage.getItem('isKioskMode') === 'true';
      if (!isKiosk || isKioskAttractActive) return;

      const resetTimer = () => {
          if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
          
          let timeoutMs = 3 * 60 * 1000; // default 3 minutes
          
          // Se siamo all'interno di scelta del menu ('menu-type'), scelta degli ingredienti ('builder') o scelta dei prodotti ('intro')
          if (['intro', 'menu-type', 'builder'].includes(view)) {
              timeoutMs = 5 * 60 * 1000; // 5 minuti richiesti
          }

          inactivityTimer.current = setTimeout(() => {
              clearSessionRef.current();
              setIsKioskAttractActiveRef.current(true);
              setViewRef.current('intro');
          }, timeoutMs);
      };

      const handleActivity = () => {
          resetTimer();
      };

      window.addEventListener('mousedown', handleActivity);
      window.addEventListener('touchstart', handleActivity);
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keydown', handleActivity);
      
      resetTimer(); // Start timer

      return () => {
          window.removeEventListener('mousedown', handleActivity);
          window.removeEventListener('touchstart', handleActivity);
          window.removeEventListener('mousemove', handleActivity);
          window.removeEventListener('keydown', handleActivity);
          if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      };
  }, [settings.isKioskMode, isKioskAttractActive, view]);
  
  const [user, setUser] = useState<User | null>(null);
  const [currentUserTenantId, setCurrentUserTenantId] = useState<string | null>(null);
  const [currentUserCity, setCurrentUserCity] = useState<string | null>(null);
  
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(() => {
      if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const trackId = urlParams.get('trackOrder');
          if (trackId) return trackId;
          
          if (window.location.pathname.startsWith('/track/')) {
              const parts = window.location.pathname.split('/');
              if (parts.length >= 4) {
                  const tenantId = parts[2];
                  const orderId = parts[3];
                  if (tenantId && orderId) {
                      localStorage.setItem('APP_TENANT_ID', tenantId);
                      return orderId;
                  }
              }
          }
      }
      return loadState('app_tracking_order_id', null);
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [menus, setMenus] = useState<MenuConfig[]>([]);
  const [categories, setCategories] = useState<CategoryConfig[]>([]);
  const [riders, setRiders] = useState<RiderProfile[]>([]);
  const [liveOrders, setLiveOrders] = useState<LiveOrder[]>([]);
  const [publicStats, setPublicStats] = useState<PublicOrderStat[]>([]); 
  
  // --- ORDER HISTORY GLOBAL STATE ---
  const [orderHistory, setOrderHistory] = useState<LiveOrder[]>([]);
  const [historyFilterMode, setHistoryFilterMode] = useState<'LAST_500' | 'TODAY' | 'WEEK' | 'MONTH' | 'ALL'>('TODAY');
  const [historyLimit, setHistoryLimit] = useState(50); // Reduced from 200 for better performance on old devices
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historySubscriptionActive, setHistorySubscriptionActive] = useState(false);

  // AUTO-FETCH HISTORY AFTER 30 SECONDS
  useEffect(() => {
      const timer = setTimeout(() => {
          if (!historySubscriptionActive) {
              console.log("Context: Auto-starting history download (30s elapsed)...");
              setHistorySubscriptionActive(true);
          }
      }, 30000);
      return () => clearTimeout(timer);
  }, [historySubscriptionActive]);
  
  // LOG SITE VISIT
  useEffect(() => {
      const tenantId = getTenantId() || 'root';
      const visitKey = `visited_today_${tenantId}_${new Date().toLocaleDateString('en-CA')}`;
      if (!localStorage.getItem(visitKey)) {
          const urlParams = new URLSearchParams(window.location.search);
          const refParam = urlParams.get('ref');
          logSiteVisit(refParam || undefined);
          localStorage.setItem(visitKey, 'true');
      }
  }, [tenantVersion]);

  // HISTORY SUBSCRIPTION EFFECT
  useEffect(() => {
      if (!user || !historySubscriptionActive) return;
      
      console.log(`Context: Subscribing to History [${historyFilterMode}, Limit: ${historyLimit}]`);
      setIsHistoryLoading(true);
      
      let unsub: () => void;

      const getStartOfWeek = () => {
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); 
        d.setHours(0,0,0,0);
        d.setDate(diff);
        return d.getTime();
      };
    
      const getStartOfMonth = () => {
        const d = new Date();
        d.setDate(1);
        d.setHours(0,0,0,0);
        return d.getTime();
      };

      const getStartOfWorkingDay = () => {
        const d = new Date();
        const { start } = getWorkingDayBoundaries(d, settings.openingHours);
        return start;
      };

      if (historyFilterMode === 'LAST_500') {
          unsub = subscribeToOrderHistory(historyLimit, (data) => {
              setOrderHistory(data);
              setIsHistoryLoading(false);
          });
      } else if (historyFilterMode === 'TODAY') {
          unsub = subscribeToOrdersSince(getStartOfWorkingDay(), (data) => {
              setOrderHistory(data);
              setIsHistoryLoading(false);
          });
      } else if (historyFilterMode === 'WEEK') {
          unsub = subscribeToOrdersSince(getStartOfWeek(), (data) => {
              setOrderHistory(data);
              setIsHistoryLoading(false);
          });
      } else if (historyFilterMode === 'MONTH') { 
          unsub = subscribeToOrdersSince(getStartOfMonth(), (data) => {
              setOrderHistory(data);
              setIsHistoryLoading(false);
          });
      } else { // ALL
          // Use subscribeToOrderHistory with a large limit to ensure we get ALL orders
          // subscribeToOrdersSince(0) might have a default limit in dbService
          unsub = subscribeToOrderHistory(10000, (data) => {
              setOrderHistory(data);
              setIsHistoryLoading(false);
          });
      }

      return () => { if (unsub) unsub(); };
  }, [user, historySubscriptionActive, historyFilterMode, historyLimit, tenantVersion]);

  const [optimisticCompletedIds, setOptimisticCompletedIds] = useState<string[]>([]);
  
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>({ shopAddress: "", zones: [] });
  const [graphicsSettings, setGraphicsSettings] = useState<MenuGraphicsSettings>(() => loadState('app_graphics', { 
      sweetTitle: "", sweetImage: "", saltyTitle: "", saltyImage: "", 
      appColors: { primary: '#4f46e5', secondary: '#111827', background: '#111827', surface: '#1f2937', text: '#f9fafb' } 
  }));
  
  const [initialLoading, setInitialLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [graphicsLoaded, setGraphicsLoaded] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [menusLoaded, setMenusLoaded] = useState(false);
  const [authReady, setAuthReady] = useState(false); 
  const [isSuperUser, setIsSuperUser] = useState(false);
  
  const isDataReady = useMemo(() => {
    return !initialLoading && authReady && settingsLoaded && graphicsLoaded && productsLoaded && categoriesLoaded && menusLoaded;
  }, [initialLoading, authReady, settingsLoaded, graphicsLoaded, productsLoaded, categoriesLoaded, menusLoaded]);
  
  // UNLOG VISIT IF ADMIN
  useEffect(() => {
      if (!authReady) return;
      if (isSuperUser || currentUserTenantId || view === 'admin' || view === 'rider-locked' || view === 'rider') {
          const tenantId = getTenantId() || 'root';
          const visitKey = `visited_today_${tenantId}_${new Date().toLocaleDateString('en-CA')}`;
          if (localStorage.getItem(visitKey) === 'true') {
              unlogSiteVisit();
              localStorage.setItem(visitKey, 'unlogged');
          }
      }
  }, [authReady, isSuperUser, currentUserTenantId, view]);

  const [orderContext, setOrderContext] = useState<OrderContext>(() => loadState('app_context', 'DINE_IN'));
  const [pendingContext, setPendingContext] = useState<OrderContext | null>(null);
  const [menuType, setMenuType] = useState<MenuType>(() => loadState('app_menu_type', 'SWEET'));
  
  const cart = useAppStore((state: any) => state.cart);
  const setCart = useAppStore((state: any) => state.setCart);
  
  const [customerName, setCustomerName] = useState(() => loadState('app_customer_name', ''));
  const [customerPhone, setCustomerPhone] = useState(() => loadState('app_customer_phone', ''));
  const [customerBirthDate, setCustomerBirthDate] = useState(() => loadState('app_customer_birth_date', ''));
  const [deliveryAddress, setDeliveryAddress] = useState(() => loadState('app_delivery_address', ''));
  const [intercomName, setIntercomName] = useState(() => loadState('app_intercom', ''));
  
  const [validatedDelivery, setValidatedDelivery] = useState<{ zone: DeliveryZone; distance: number; address: string } | null>(() => loadState('app_validated_delivery', null));
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'POS' | 'NEXI' | 'SATISPAY' | 'PAYPAL' | null>(null);
  
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [redeemLoyalty, setRedeemLoyalty] = useState(false);
  const [hasActiveSuperReward, setHasActiveSuperReward] = useState(false);
  const [superRewardExpiryDate, setSuperRewardExpiryDate] = useState<number | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedIngredients, setSelectedIngredients] = useState<Ingredient[]>([]);
  const [editingCartId, setEditingCartId] = useState<string | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [servingStyle, setServingStyle] = useState<string | null>(null);

  const [toast, setToast] = useState({ show: false, message: '' });
  
  const [isKioskMode, setIsKioskModeState] = useState(() => localStorage.getItem('isKioskMode') === 'true');
  const [isDeviceMuted, setIsDeviceMuted] = useState(() => loadState('device_muted', false));

  const setIsKioskMode = (val: boolean) => {
      setIsKioskModeState(val);
      localStorage.setItem('isKioskMode', String(val));
  };
  
  const [language, setLanguage] = useState<Language>(() => loadState('app_language', 'it'));

  const isProRetailMode = settings.isProRetailMode || false;
  
  useEffect(() => {
      saveState('app_view', view);
      saveState('app_context', orderContext);
      saveState('app_menu_type', menuType);
      saveState('app_customer_name', customerName);
      saveState('app_customer_phone', customerPhone);
      saveState('app_customer_birth_date', customerBirthDate);
      saveState('app_delivery_address', deliveryAddress);
      saveState('app_validated_delivery', validatedDelivery);
      saveState('app_intercom', intercomName);
      saveState('app_settings', settings); 
      saveState('app_graphics', graphicsSettings);
      saveState('device_muted', isDeviceMuted);
      saveState('app_language', language);
      saveState('app_tracking_order_id', trackingOrderId);
  }, [view, orderContext, menuType, cart, customerName, customerPhone, customerBirthDate, deliveryAddress, validatedDelivery, intercomName, settings, graphicsSettings, isDeviceMuted, language, trackingOrderId]);

  useEffect(() => {
      const handlePopState = (event: PopStateEvent) => {
          if (event.state && event.state.view) _setView(event.state.view);
          else _setView('intro');
      };
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => { 
      // SMART LOADING LOGIC:
      // 1. Basic requirements for ANY view (Auth + Branding)
      const baseReady = authReady && settingsLoaded && graphicsLoaded;
      
      // 2. Data requirements for CLIENT views (Menu, Cart, etc.)
      const isClientView = ['intro', 'menu-type', 'base-select', 'builder', 'cart'].includes(view);
      const dataReady = productsLoaded && categoriesLoaded && menusLoaded;

      // 3. Admin bypass: If we are already logged in as admin, we don't wait for client data
      const isLikelyAdmin = !!user && !!currentUserTenantId;

      // 4. Logic: 
      // - If we have baseReady, we can show the app shell.
      // - We only keep the loader if we are in a client view and data is NOT ready yet.
      const canHideLoader = baseReady && (isLikelyAdmin || !isClientView || dataReady);

      if (canHideLoader) {
          // Use a short delay to ensure React has painted the state
          const timer = setTimeout(() => {
              console.log("Context: Data is ready, hiding loader.");
              setInitialLoading(false);
          }, 100);
          return () => clearTimeout(timer);
      } else {
          // If we are NOT ready, ensure initialLoading is true
          setInitialLoading(true);
      }
  }, [authReady, settingsLoaded, graphicsLoaded, productsLoaded, categoriesLoaded, menusLoaded, view, user, currentUserTenantId]);

  // TENANT SWITCH LISTENER
  useEffect(() => {
      const handleTenantChange = () => {
          const newTenantId = getTenantId();
          console.log(`Context: Tenant Changed detected to ${newTenantId}. Resetting state...`);
          
          // Reset crucial data states to prevent stale data
          setProducts([]);
          setMenus([]);
          setIngredients([]);
          setCategories([]);
          setLiveOrders([]);
          setCart([]); // Clear cart to avoid mismatch
          
          // Only reset settings if we are actually switching to a different tenant
          // This prevents the logo from disappearing during a simple reload or minor update
          setSettings(prev => ({ ...prev, appName: prev.appName || "Caricamento..." })); 
          
          setSettingsLoaded(false);
          setGraphicsLoaded(false);
          setProductsLoaded(false);
          setCategoriesLoaded(false);
          setMenusLoaded(false);
          setAuthReady(false); 
          setInitialLoading(true);
          
          // Trigger effect re-run
          setTenantVersion(prev => prev + 1);
      };
      
      window.addEventListener('tenant-changed', handleTenantChange);
      return () => window.removeEventListener('tenant-changed', handleTenantChange);
  }, []);

  useEffect(() => {
      const unlock = () => {
          unlockAudioContext();
          window.removeEventListener('click', unlock);
          window.removeEventListener('touchstart', unlock);
      };
      window.addEventListener('click', unlock);
      window.addEventListener('touchstart', unlock);
      return () => {
          window.removeEventListener('click', unlock);
          window.removeEventListener('touchstart', unlock);
      };
  }, []);

  // MAIN SUBSCRIPTION EFFECT
  useEffect(() => {
    console.log(`Context: Subscribing to Tenant Version: ${tenantVersion}`);
    
    // REDUCED SAFETY TIMER: 5000ms.
    // If Firestore is slow, we force the UI to show up anyway.
    const safetyTimer = setTimeout(() => {
        console.warn("Context: Safety timer expired. Forcing UI load.");
        setSettingsLoaded(true);
        setGraphicsLoaded(true);
        setProductsLoaded(true);
        setCategoriesLoaded(true);
        setMenusLoaded(true);
        setAuthReady(true); 
    }, 5000); 

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
        setUser(u);
        if (u && u.email) {
            const superAdminB64 = "bm9zX0Bob3RtYWlsLml0";
            const normEmail = u.email.trim().toLowerCase();
            const isSuper = normEmail === "nos_@hotmail.it" || btoa(normEmail) === superAdminB64 || normEmail === "nos_1@icloud.com";
            
            if (isSuper) {
                setIsSuperUser(true);
                setCurrentUserTenantId('root');
            } else {
                setIsSuperUser(false);
                const tenant = await getAdminTenant(u.email);
                setCurrentUserTenantId(tenant);
                if (tenant) {
                    const meta = await getTenantMetadata(tenant);
                    if (meta) setCurrentUserCity(meta.city || null);
                }
            }
        } else {
            setCurrentUserTenantId(null);
            setCurrentUserCity(null);
            setIsSuperUser(false);
        }
        setAuthReady(true); 
    });

    const unsubProd = subscribeToProducts((data) => {
        setProducts(data);
        setProductsLoaded(true);
    });
    const unsubIng = subscribeToIngredients(setIngredients);
    const unsubMenu = subscribeToMenus((data) => {
        setMenus(data);
        setMenusLoaded(true);
    });
    const unsubCat = subscribeToCategories((data) => {
        setCategories(data);
        setCategoriesLoaded(true);
    });
    const unsubDel = subscribeToDeliverySettings(setDeliverySettings);
    
    const unsubGraph = subscribeToGraphicsSettings((settings) => {
        if (settings && Object.keys(settings).length > 0) {
            setGraphicsSettings(prev => ({ ...settings, appColors: settings.appColors || prev.appColors }));
        }
        setGraphicsLoaded(true);
    });
    
    const unsubStats = subscribeToPublicStats(50, setPublicStats);

    const unsubSet = subscribeToSettings((newSettings) => {
        if (newSettings && Object.keys(newSettings).length > 0) {
            setSettings(prev => ({ 
                ...prev, 
                ...newSettings,
                servingOptions: newSettings.servingOptions || DEFAULT_SERVING_OPTIONS
            }));
        }
        setSettingsLoaded(true);
    });

    return () => { 
        clearTimeout(safetyTimer);
        unsubAuth(); unsubProd(); unsubIng(); unsubMenu(); unsubCat(); unsubDel(); unsubGraph(); unsubSet(); unsubStats();
    };
  }, [tenantVersion]); // RE-RUN ON TENANT CHANGE

  useEffect(() => {
      let unsubRiders = () => {};
      let unsubLive = () => {};
      if (user) {
          unsubLive = subscribeToPendingOrders((orders) => {
              setLiveOrders(orders);
              setOptimisticCompletedIds(prev => prev.filter(id => orders.some(o => o && o.id === id)));
          });
          unsubRiders = subscribeToRiders(setRiders);
          return () => { unsubLive(); unsubRiders(); };
      } else {
          setLiveOrders([]); 
      }
  }, [user, tenantVersion]);

  useEffect(() => {
      if (customerPhone && customerPhone.length > 5) {
          const checkStatus = async () => {
              const cleanPhone = customerPhone.replace(/[^0-9]/g, '');
              const fullPhone = cleanPhone.startsWith('39') ? cleanPhone : '39' + cleanPhone;
              
              const customer = await getCustomerByPhone(fullPhone);
              
              if (customer) {
                  setLoyaltyPoints(customer.loyaltyPoints || 0);
                  if (customer.birthDate && !customerBirthDate) {
                      setCustomerBirthDate(customer.birthDate);
                  }
                  if (customer.name && !customerName) {
                      setCustomerName(customer.name);
                  }
                  if (customer.superRewardExpiry && customer.superRewardExpiry > Date.now()) {
                      setHasActiveSuperReward(true);
                      setSuperRewardExpiryDate(customer.superRewardExpiry);
                  } else {
                      setHasActiveSuperReward(false);
                      setSuperRewardExpiryDate(null);
                  }
              } else {
                  setLoyaltyPoints(0);
                  setHasActiveSuperReward(false);
                  setSuperRewardExpiryDate(null);
              }
          };
          checkStatus();
      } else {
          setLoyaltyPoints(0);
          setHasActiveSuperReward(false);
          setSuperRewardExpiryDate(null);
      }
  }, [customerPhone, tenantVersion]);

  useEffect(() => {
    const colors = graphicsSettings.appColors || { primary: '#4f46e5', secondary: '#111827', background: '#111827', surface: '#1f2937', text: '#f9fafb' };
    const root = document.documentElement;
    root.style.setProperty('--app-primary', colors.primary);
    root.style.setProperty('--app-secondary', colors.secondary);
    root.style.setProperty('--app-bg', colors.background);
    root.style.setProperty('--app-surface', colors.surface);
    root.style.setProperty('--app-text', colors.text);
  }, [graphicsSettings.appColors]);

  const t = (key: string, params?: Record<string, string>): string => {
      let str = (TRANSLATIONS[language] && TRANSLATIONS[language][key]) || TRANSLATIONS['it']?.[key] || key;
      if (params) {
          Object.entries(params).forEach(([k, v]) => {
              str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
          });
      }
      return str;
  };

  // --- CART TOTAL & SURCHARGE LOGIC ---
  const { total, surcharge, promoDiscount } = useMemo(() => {
    let itemsTotal = cart.reduce((acc: number, item: CartItem) => acc + (item.itemTotal * (item.quantity || 1)), 0);
    
    // 0. CALCOLO SCONTO QUANTITA' PROMOZIONALE (Es. Prendi 6 Paghi 5)
    let calculatedPromoDiscount = 0;
    
    // Create a set of handled cart items to avoid double discounting
    const handledCartItemIds = new Set<string>();
    
    // Find all products in cart that act as promo "anchors"
    const promoAnchors = cart.filter((i: CartItem) => 
        i.product.promoConfig && 
        i.product.promoConfig.buyQuantity > 0 && 
        i.product.promoConfig.freeQuantity > 0 &&
        i.product.promoConfig.buyQuantity > i.product.promoConfig.freeQuantity
    );
    
    // Deduplicate anchors by product ID so we only evaluate each promo rule once
    const uniqueAnchors = Array.from(new Map<string, CartItem>(promoAnchors.map((item: CartItem) => [item.product.id, item])).values());
    
    for (const anchor of uniqueAnchors) {
        const promoConfig = anchor.product.promoConfig!; // We know it exists from filter
        
        // Determine which product IDs are part of this promo group
        const groupProductIds = new Set<string>([anchor.product.id]);
        if (promoConfig.crossGroupProductIds) {
            promoConfig.crossGroupProductIds.forEach((id: string) => groupProductIds.add(id));
        }
        
        // Find all items in the cart that belong to this group AND haven't been discounted yet
        const groupItems = cart.filter((item: CartItem) => 
            groupProductIds.has(item.product.id) && !handledCartItemIds.has(item.cartId)
        );
        
        if (groupItems.length === 0) continue;
        
        const totalQty = groupItems.reduce((sum: number, i: CartItem) => sum + (i.quantity || 1), 0);
        const timesPromoApplied = Math.floor(totalQty / promoConfig.buyQuantity);
        const freeItemsCount = timesPromoApplied * promoConfig.freeQuantity;
        
        if (freeItemsCount > 0) {
            // Mark all items in this group as handled so they aren't processed by another anchor's rule
            groupItems.forEach((i: CartItem) => handledCartItemIds.add(i.cartId));
            
            // Unroll items to discount the cheapest individual items
            const individualItems: { price: number }[] = [];
            for (const item of groupItems) {
                for (let i = 0; i < (item.quantity || 1); i++) {
                    individualItems.push({ price: item.itemTotal });
                }
            }
            
            // Sort by price ascending (so we give away the cheapest items)
            individualItems.sort((a, b) => a.price - b.price);
            
            for (let i = 0; i < freeItemsCount; i++) {
                if (i < individualItems.length) {
                    calculatedPromoDiscount += individualItems[i].price;
                }
            }
        }
    }
    
    // We apply standard discount AFTER promo discounts
    let adjustedSubtotal = itemsTotal - calculatedPromoDiscount;

    // 1. Applica sconto percentuale (App Discount)
    let applicableDiscountRate = 0;
    
    if (settings.autoDiscountRates) {
        if (orderContext === 'DELIVERY') applicableDiscountRate = settings.autoDiscountRates.delivery || 0;
        else if (orderContext === 'TAKE_AWAY') applicableDiscountRate = settings.autoDiscountRates.takeAway || 0;
        else if (orderContext === 'DINE_IN') applicableDiscountRate = settings.autoDiscountRates.dineIn || 0;
    } else if (settings.discountApplicability) {
        // Fallback backward compatibility
        if (orderContext === 'DELIVERY' && settings.discountApplicability.delivery) applicableDiscountRate = settings.deliveryDiscount || 0;
        else if (orderContext === 'TAKE_AWAY' && settings.discountApplicability.takeAway) applicableDiscountRate = settings.deliveryDiscount || 0;
        else if (orderContext === 'DINE_IN' && settings.discountApplicability.dineIn) applicableDiscountRate = settings.deliveryDiscount || 0;
    } else {
        if (orderContext === 'DELIVERY') applicableDiscountRate = settings.deliveryDiscount || 0;
    }

    let discountedTotal = adjustedSubtotal;
    if (applicableDiscountRate > 0) {
        discountedTotal -= adjustedSubtotal * (applicableDiscountRate / 100);
    }

    let calculatedSurcharge = 0;

    // 2. Controllo Minimo Ordine (solo se DELIVERY e abbiamo una zona valida)
    if (orderContext === 'DELIVERY' && validatedDelivery && !isProRetailMode) {
        const minOrder = validatedDelivery.zone.minOrder || 0;
        if (minOrder > 0) {
            const difference = minOrder - adjustedSubtotal;
            if (difference > 0.01) { // Usiamo 0.01 per tolleranza float
                calculatedSurcharge = difference;
            }
        }
    }
    
    return { total: itemsTotal, surcharge: calculatedSurcharge, promoDiscount: calculatedPromoDiscount };
  }, [cart, orderContext, validatedDelivery, settings, isProRetailMode]);

  const isDeliveryAvailable = useMemo(() => {
      // 1. Explicitly disabled in settings
      if (settings.isDeliveryEnabled === false) return false;
      
      // 2. Retail Mode: usually always available unless explicitly disabled
      if (isProRetailMode) return true;

      // 3. Scheduled Mode (Restaurants)
      if (settings.deliveryScheduleMode === 'SCHEDULED') {
          // If schedule is missing or invalid, fallback to available to prevent accidental lockout
          if (!settings.deliveryStartTime || !settings.deliveryEndTime) return true;
          
          const now = new Date();
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const [sH, sM] = settings.deliveryStartTime.split(':').map(Number);
          const [eH, eM] = settings.deliveryEndTime.split(':').map(Number);
          
          if (isNaN(sH) || isNaN(eH)) return true; // Invalid time, fallback true
          
          const startMinutes = sH * 60 + sM;
          const endMinutes = eH * 60 + eM;
          
          return endMinutes < startMinutes ? (currentMinutes >= startMinutes || currentMinutes < endMinutes) : (currentMinutes >= startMinutes && currentMinutes < endMinutes);
      }
      
      // Default (Manual Mode)
      return true;
  }, [settings, isProRetailMode, timeTick]);

  const shopStatus = useMemo(() => {
      if (isKioskMode) return { isOpen: true, currentSchedule: t('status_open') + " (Kiosk)" };
      
      const now = new Date();
      const logicalNow = new Date(now);
      if (logicalNow.getHours() < 6) logicalNow.setDate(logicalNow.getDate() - 1);
      const todayStr = logicalNow.toLocaleDateString('en-CA'); 
      const specialClosure = settings.specialClosures?.find(c => c.date === todayStr);
      
      if (specialClosure) {
          return { 
              isOpen: false, 
              currentSchedule: specialClosure.reason 
                ? `${t('closed_extra')} (${specialClosure.reason})` 
                : t('closed_extra')
          };
      }

      const hours = settings.openingHours || [];
      if (hours.length === 0) return { isOpen: true, currentSchedule: t('status_open') }; 
      
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const days = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
      const todayName = days[now.getDay()];
      const yesterdayName = days[(now.getDay() + 6) % 7];

      const checkIsOpen = (dayConfig: OpeningDay | undefined, overnight = false) => {
          if (!dayConfig || dayConfig.status === 'closed') return false;
          return dayConfig.slots.some(slot => {
              const [sH, sM] = slot.start.split(':').map(Number);
              const [eH, eM] = slot.end.split(':').map(Number);
              const start = sH * 60 + sM;
              const end = eH * 60 + eM;
              if (overnight) return end < start ? currentMinutes < end : false;
              return end < start ? currentMinutes >= start : (currentMinutes >= start && currentMinutes < end);
          });
      };

      if (checkIsOpen(hours.find(d => d.dayName === todayName), false)) return { isOpen: true, currentSchedule: t('status_open') };
      if (checkIsOpen(hours.find(d => d.dayName === yesterdayName), true)) return { isOpen: true, currentSchedule: t('status_open') };
      return { isOpen: false, currentSchedule: t('status_closed') };
  }, [settings.openingHours, settings.specialClosures, isKioskMode, language, timeTick]);

  const showToast = (message: string) => { setToast({ show: true, message }); setTimeout(() => setToast({ show: false, message: '' }), 3000); };
  
  const playNotificationSound = () => {
      const type = settings.soundSettings?.soundType || 'doorbell';
      const volume = settings.soundSettings?.volume !== undefined ? settings.soundSettings.volume : 0.5;
      
      if (settings.soundSettings?.enabled !== false) {
          playSynthesizedSound(type, volume);
      }
  };

  const handleOptimisticComplete = (orderId: string) => {
      setOptimisticCompletedIds(prev => [...prev, orderId]);
  };
  const revertOptimisticComplete = (orderId: string) => {
      setOptimisticCompletedIds(prev => prev.filter(id => id !== orderId));
  };

  const pendingOrdersCount = liveOrders.filter(o => o && o.status === 'pending' && !optimisticCompletedIds.includes(o.id)).length;

  const resolveLang = (obj: any, field: string): string => {
      if (!obj) return '';
      
      // 1. Direct match in languageData
      if (obj.languageData && obj.languageData[language] && obj.languageData[language][field]) {
          return obj.languageData[language][field];
      }
      
      // 2. Bidirectional fallback for name <-> title in languageData
      if (field === 'name' && obj.languageData && obj.languageData[language] && obj.languageData[language]['title']) {
          return obj.languageData[language]['title'];
      }
      if (field === 'title' && obj.languageData && obj.languageData[language] && obj.languageData[language]['name']) {
          return obj.languageData[language]['name'];
      }

      // 3. Check automatic dictionary for common menu/category standard names (e.g. Dolci -> Desserts, Salato -> Savory, Bevande -> Drinks)
      const baseValue = obj[field] || (field === 'name' ? obj.title : (field === 'title' ? obj.name : ''));
      if (baseValue && typeof baseValue === 'string') {
          const dictTranslation = getStandardTranslation(baseValue, language);
          if (dictTranslation) {
              return dictTranslation;
          }
      }

      return obj[field] || (field === 'name' ? obj.title : (field === 'title' ? obj.name : '')) || '';
  };

  const value = useMemo(() => ({
      view, setView, adminSubView, setAdminSubView, isOrderListOpen, setIsOrderListOpen, isKioskAttractActive, setIsKioskAttractActive, user, currentUserTenantId, currentUserCity,
      products, ingredients, menus, categories, settings, deliverySettings, graphicsSettings, liveOrders, publicStats,
      optimisticCompletedIds, handleOptimisticComplete, revertOptimisticComplete, pendingOrdersCount,
      initialLoading, menuLoading,
      orderContext, setOrderContext, pendingContext, setPendingContext, menuType, setMenuType, cart, setCart, 
      cartTotal: total, // Export the calculated total
      promoQuantityDiscount: promoDiscount,
      smallOrderSurcharge: surcharge, // Export the surcharge
      deliveryAddress, setDeliveryAddress, validatedDelivery, setValidatedDelivery, intercomName, setIntercomName,
      customerName, setCustomerName, customerPhone, setCustomerPhone, customerBirthDate, setCustomerBirthDate, paymentMethod, setPaymentMethod,
      loyaltyPoints, setLoyaltyPoints, redeemLoyalty, setRedeemLoyalty, hasActiveSuperReward, setHasActiveSuperReward, superRewardExpiryDate, setSuperRewardExpiryDate,
      selectedProduct, setSelectedProduct, selectedIngredients, setSelectedIngredients, editingCartId, setEditingCartId, editingOrderId, setEditingOrderId, servingStyle, setServingStyle,
      playNotificationSound, showToast, toast, isKioskMode, setIsKioskMode, isProRetailMode, shopStatus, isDeliveryAvailable,
      isDeviceMuted, setIsDeviceMuted,
      language, setLanguage, t, resolveLang,
      trackingOrderId, setTrackingOrderId,
      clearSession,
      isDataReady,
      riders,
      isSuperUser,
      settingsLoaded,
      graphicsLoaded,
      // History
      orderHistory, historyFilterMode, setHistoryFilterMode, historyLimit, setHistoryLimit, isHistoryLoading, setHistorySubscriptionActive
  }), [view, adminSubView, isOrderListOpen, isKioskAttractActive, setIsKioskAttractActive, user, currentUserTenantId, products, ingredients, menus, categories, settings, deliverySettings, graphicsSettings, liveOrders, publicStats, optimisticCompletedIds, pendingOrdersCount, initialLoading, menuLoading, orderContext, pendingContext, menuType, cart, total, surcharge, deliveryAddress, validatedDelivery, intercomName, customerName, customerPhone, customerBirthDate, paymentMethod, loyaltyPoints, redeemLoyalty, hasActiveSuperReward, superRewardExpiryDate, selectedProduct, selectedIngredients, editingCartId, editingOrderId, servingStyle, toast, isKioskMode, isProRetailMode, shopStatus, isDeliveryAvailable, isDeviceMuted, language, trackingOrderId, clearSession, isDataReady, isSuperUser, riders, currentUserCity, orderHistory, historyFilterMode, historyLimit, isHistoryLoading]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (context === undefined) throw new Error('useApp must be used within an AppProvider');
    return context;
};
